import { MinMax, TableV3FilterItem } from 'common/table/types/TableV3';
import { tableFilterItemsToMinMax } from 'common/table/utils/minMaxTableFilterItems';
import {
  buildSelectionCondition,
  CandidateFilterActiveModes,
  findRangeConditionShownInColumn,
  getActiveMode,
  isColumnCondition,
  isRange,
  isSelection,
  settingsKey,
  withFreeIds
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import {
  columnMinMaxToRangeCondition,
  rangeConditionToColumnMinMax
} from 'modules/candidate-profile/tables/filters/rangeConditionColumnMinMax';
import { searchColumnForField } from 'modules/candidate-profile/tables/filters/searchesToConditions';
import { withLegacyTimeInRoleBandsAsRange } from 'modules/candidate-profile/tables/filters/legacyTimeInRoleBands';
import {
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateRangeFilter,
  SourcingFilterField
} from 'modules/candidate-profile/types/CandidateSearchFilter';

const SELECTION_TYPES = [
  CandidateFilterValueType.TEXT,
  CandidateFilterValueType.TEXT_ARRAY
];

type SyncParams = {
  conditions: CandidateFilterState[];
  filterValues: Record<string, TableV3FilterItem[]>;
  activeModes: CandidateFilterActiveModes;
  availableFilters: SourcingFilterField[];
};

export type SyncResult = {
  conditions: CandidateFilterState[];
  passthrough: Record<string, TableV3FilterItem[]>;
};

/**
 * The table reports only the active mode's condition per column. Other modes
 * are carried through untouched, which is what lets one field hold an include
 * and an exclude at once -- as is a second condition on the same field and
 * mode, which the column has no room for and which is a chip only.
 */
export const syncConditionsFromTable = ({
  conditions,
  filterValues,
  activeModes,
  availableFilters
}: SyncParams): SyncResult => {
  // A text column is searched, not filtered.
  const selectable = new Map(
    availableFilters
      .filter(
        (available) =>
          SELECTION_TYPES.includes(available.type) &&
          !searchColumnForField(available.field, availableFilters)
      )
      .map((available) => [available.field, available])
  );

  const booleans = new Map(
    availableFilters
      .filter(
        (available) => available.type === CandidateFilterValueType.BOOLEAN
      )
      .map((available) => [available.field, available])
  );

  const readColumnMinMax = (field: string) =>
    tableFilterItemsToMinMax(
      withLegacyTimeInRoleBandsAsRange(filterValues[field] ?? [])
    );

  const ranges = new Map(
    availableFilters
      .filter((available) => available.range)
      .map((available) => [available.field, available])
  );

  const passthrough = Object.fromEntries(
    Object.entries(filterValues).filter(
      ([field]) =>
        !selectable.has(field) && !booleans.has(field) && !ranges.has(field)
    )
  );

  const isReadFromColumn = (condition: CandidateFilterState): boolean => {
    // A column holds one Yes/No, so a second one on the field is a chip only.
    if (condition.type === CandidateFilterValueType.BOOLEAN) {
      return (
        booleans.has(condition.field) &&
        conditions.find((each) => each.field === condition.field) === condition
      );
    }

    if (isRange(condition)) {
      return (
        ranges.has(condition.field) &&
        findRangeConditionShownInColumn(conditions, condition.field) ===
          condition
      );
    }

    return (
      isSelection(condition) &&
      selectable.has(condition.field) &&
      condition.mode === getActiveMode(activeModes, condition.field) &&
      isColumnCondition(conditions, condition)
    );
  };

  const selectionsFromColumns = [...selectable.values()].flatMap(
    (available) => {
      const condition = buildSelectionCondition(
        available,
        getActiveMode(activeModes, available.field),
        filterValues[available.field] ?? []
      );

      return condition ? [condition] : [];
    }
  );

  const booleansFromColumns = [...booleans.values()].flatMap(
    (available): CandidateFilterState[] => {
      const [item] = filterValues[available.field] ?? [];
      if (!item) return [];

      return [
        {
          id: available.field,
          field: available.field,
          ...(available.vectorDbKey
            ? { vectorDbKey: available.vectorDbKey }
            : {}),
          type: CandidateFilterValueType.BOOLEAN,
          value: item.value === 'true'
        }
      ];
    }
  );

  const readRangeConditionsFromColumns = [...ranges.values()].flatMap(
    (available): CandidateFilterState[] => {
      const minMax = columnMinMaxToRangeCondition(
        readColumnMinMax(available.field),
        available.range
      );
      if (minMax.min === undefined && minMax.max === undefined) return [];

      return [
        {
          id: available.field,
          field: available.field,
          ...(available.vectorDbKey
            ? { vectorDbKey: available.vectorDbKey }
            : {}),
          type: available.type as CandidateRangeFilter['type'],
          ...minMax
        }
      ];
    }
  );

  const isSameMinMax = (a: MinMax, b: MinMax) =>
    a.min === b.min && a.max === b.max;

  const columnStillShowsCondition = (
    condition: CandidateFilterState,
    rebuilt: CandidateFilterState
  ) =>
    isRange(condition)
      ? isSameMinMax(
          rangeConditionToColumnMinMax(
            condition,
            ranges.get(condition.field)?.range
          ),
          readColumnMinMax(condition.field)
        )
      : settingsKey(rebuilt) === settingsKey(condition);

  const fromColumns: CandidateFilterState[] = [
    ...selectionsFromColumns,
    ...booleansFromColumns,
    ...readRangeConditionsFromColumns
  ];
  const fromColumnByField = new Map(
    fromColumns.map((condition) => [condition.field, condition])
  );

  // Replaced in place, under the same id, so Or groups keep naming them.
  const updated = conditions.flatMap((condition) => {
    if (!isReadFromColumn(condition)) return [condition];

    const fromColumn = fromColumnByField.get(condition.field);
    if (!fromColumn) return [];

    // Same object when unchanged: a turn in flight pairs chips by identity.
    const rebuilt = { ...fromColumn, id: condition.id };
    return [
      columnStillShowsCondition(condition, rebuilt) ? condition : rebuilt
    ];
  });

  const readFields = new Set(
    conditions.filter(isReadFromColumn).map((condition) => condition.field)
  );
  const added = fromColumns.filter(
    (condition) => !readFields.has(condition.field)
  );

  return {
    conditions: [...updated, ...withFreeIds(added, updated)],
    passthrough
  };
};
